import router from "@/router";
import moment from "moment";
import { getMakeList, getModelList, getSeriesList } from "../../api/common";
import { globalSearchSeriesFormat } from "../../api/inventory";

const findByName = (list, keyword) =>
  list.find((item) =>
    item.name.toLowerCase().replace(/\s+/g, "").includes(keyword.toLowerCase())
  );

const state = () => {
  return {
    inventory: {
      filterData: {
        makeId: "",
        modelId: "",
        seriesId: "",
        search: "",
      },
      isFiltered: false,
      isLoading: false,
    },
    threshold: {
      filterData: {
        makeId: "",
        modelId: "",
        seriesId: "",
        search: "",
      },
      isFiltered: false,
      isLoading: false,
    },
    backOrder: {
      filterData: {
        makeId: "",
        modelId: "",
        seriesId: "",
        search: "",
      },
      isFiltered: false,
      isLoading: false,
    },
    listPrice: {
      filterData: {
        selectedDates: [
          moment().format("YYYY-MM-DD"),
          moment().add(1, "days").format("YYYY-MM-DD"),
        ],
        makeId: "",
        modelId: "",
        seriesId: "",
        search: "",
      },
      showDatePicker: true,
      isFiltered: false,
      isLoading: false,
    },
    makeList: [],
  };
};

const mutations = {
  SET_SELECTED_FILTER: (state, { moduleName, filterData }) => {
    state[moduleName].filterData = filterData;
  },
  SET_IS_FILTERED: (state, { moduleName, payload }) => {
    state[moduleName].isFiltered = payload;
  },
  SET_CLEAR_FILTER: (state, { moduleName, payload }) => {
    state[moduleName].filterData = payload;
  },
  SET_MAKE_LIST: (state, payload) => {
    state.makeList = payload;
  },
  SET_SEARCH_LOADING: (state, { moduleName, payload }) => {
    state[moduleName].isLoading = payload;
  },

  // listPrice
  SET_SELECTED_PRICE_LIST_DATE: (state, payload) => {
    state.listPrice.selectedDates = payload;
  },
  SET_SHOW_DATE_PICKER: (state, { moduleName, payload }) => {
    state[moduleName].showDatePicker = payload;
  },
};

const actions = {
  async setMakeList({ commit }) {
    try {
      const result = await getMakeList();
      const { code, data } = result;
      if (code === 200) {
        commit("SET_MAKE_LIST", data);
      }
    } catch (error) {
      throw new Error(error);
    }
  },
  setClearFilter({ state, commit }, { moduleName, isSearch }) {
    let payload = {
      makeId: "",
      modelId: "",
      seriesId: "",
      search: "",
    };
    if (moduleName === "listPrice" && !isSearch) {
      payload = {
        ...payload,
        selectedDates: [
          moment().format("YYYY-MM-DD"),
          moment().add(1, "days").format("YYYY-MM-DD"),
        ],
      };
    } else if (moduleName === "listPrice" && isSearch) {
      payload = {
        ...payload,
        selectedDates: state.listPrice.filterData.selectedDates,
      };
    }
    commit("SET_CLEAR_FILTER", { moduleName, payload });
  },
  async globalSearchHandler(
    { state, commit, dispatch },
    { listWords, descWords, moduleName, seriesFormatChecked }
  ) {
    // ,
    commit("SET_SEARCH_LOADING", { moduleName, payload: true });
    if (
      router.currentRoute.name !== "InventoryList" &&
      moduleName === "inventory"
    ) {
      router.push({ name: "InventoryList", query: { fromGlobalSearch: "1" } });
    }

    // Series format: "<series> . <part description>" e.g. "zre18 . front bumper".
    // Resolved before make/model/series so the result never depends on the
    // filter left over from the previous search.
    if (
      !seriesFormatChecked &&
      listWords.length === 1 &&
      !findByName(state.makeList, listWords[0])
    ) {
      const matchedSeriesFormat = await dispatch("searchBySeriesFormat", {
        series: listWords[0],
        descWords,
        moduleName,
      });
      if (matchedSeriesFormat) {
        commit("SET_SEARCH_LOADING", { moduleName, payload: false });
        return;
      }
      seriesFormatChecked = true;
    }

    let listModel = [];
    let listSeries = [];
    const makeIdSelected = state[moduleName].filterData.makeId;
    const modelIdSelected = state[moduleName].filterData.modelId;

    if (listWords.length > 0) {
      if (!makeIdSelected) {
        const make = findByName(state.makeList, listWords[0]);
        if (!make) {
          await dispatch("setClearFilter", { moduleName, isSearch: true });
          commit("SET_SEARCH_LOADING", { moduleName, payload: false });
          commit("SET_SELECTED_FILTER", {
            moduleName,
            filterData: {
              ...state[moduleName].filterData,
              search: listWords.join(" "),
            },
          });
          dispatch("setIsFiltered", { moduleName, payload: true });
          return;
        }
        await dispatch("setClearFilter", { moduleName, isSearch: true });
        if (descWords.length > 0) {
          commit("SET_SELECTED_FILTER", {
            moduleName,
            filterData: {
              ...state[moduleName].filterData,
              search: descWords[0],
            },
          });
        }
        commit("SET_SELECTED_FILTER", {
          moduleName,
          filterData: { ...state[moduleName].filterData, makeId: make.id },
        });
        if (listWords[1]) {
          const modelResult = await getModelList({ makeId: make.id });
          listModel = modelResult.data;
          const model = findByName(listModel, listWords[1]);
          if (!model) {
            await dispatch("setClearFilter", { moduleName, isSearch: true });
            commit("SET_SEARCH_LOADING", { moduleName, payload: false });
            commit("SET_SELECTED_FILTER", {
              moduleName,
              filterData: {
                ...state[moduleName].filterData,
                search: listWords.join(" "),
              },
            });
            dispatch("setIsFiltered", { moduleName, payload: true });
            return;
          }
          commit("SET_SELECTED_FILTER", {
            moduleName,
            filterData: { ...state[moduleName].filterData, modelId: model.id },
          });
          if (listWords[2]) {
            const seriesResult = await getSeriesList({
              makeId: make.id,
              modelId: model.id,
            });
            listSeries = seriesResult.data;
            const series = findByName(listSeries, listWords[2]);
            if (!series) {
              await dispatch("setClearFilter", { moduleName, isSearch: true });
              commit("SET_SEARCH_LOADING", { moduleName, payload: false });
              commit("SET_SELECTED_FILTER", {
                moduleName,
                filterData: {
                  ...state[moduleName].filterData,
                  search: listWords.join(" "),
                },
              });
              dispatch("setIsFiltered", { moduleName, payload: true });
              return;
            }
            commit("SET_SELECTED_FILTER", {
              moduleName,
              filterData: {
                ...state[moduleName].filterData,
                seriesId: series.id,
              },
            });
            dispatch("setIsFiltered", { moduleName, payload: true });
          } else {
            dispatch("setIsFiltered", { moduleName, payload: true });
          }
        } else {
          dispatch("setIsFiltered", { moduleName, payload: true });
        }
      } else {
        if (!modelIdSelected) {
          const modelResult = await getModelList({ makeId: makeIdSelected });
          listModel = modelResult.data;
          const model = findByName(listModel, listWords[0]);
          if (!model) {
            // alert(`Model ${listWords[0]} not exits in ${make.name}`);
            await dispatch("setClearFilter", { moduleName, isSearch: true });
            await dispatch("globalSearchHandler", {
              listWords,
              descWords,
              moduleName: moduleName,
              seriesFormatChecked,
            });
            return;
          }
          commit("SET_SELECTED_FILTER", {
            moduleName,
            filterData: { ...state[moduleName].filterData, modelId: model.id },
          });
          if (listWords[1]) {
            const seriesResult = await getSeriesList({
              makeId: makeIdSelected,
              modelId: model.id,
            });
            listSeries = seriesResult.data;
            const series = findByName(listSeries, listWords[1]);
            if (!series) {
              await dispatch("setClearFilter", { moduleName, isSearch: true });
              await dispatch("globalSearchHandler", {
                listWords,
                descWords,
                moduleName: moduleName,
                seriesFormatChecked,
              });
              return;
            }
            commit("SET_SELECTED_FILTER", {
              moduleName,
              filterData: {
                ...state[moduleName].filterData,
                seriesId: series.id,
              },
            });
            dispatch("setIsFiltered", { moduleName, payload: true });
          } else {
            dispatch("setIsFiltered", { moduleName, payload: true });
          }
        } else {
          if (listWords[0]) {
            const seriesResult = await getSeriesList({
              makeId: makeIdSelected,
              modelId: modelIdSelected,
            });
            listSeries = seriesResult.data;
            const series = findByName(listSeries, listWords[0]);
            if (!series) {
              await dispatch("setClearFilter", { moduleName, isSearch: true });
              await dispatch("globalSearchHandler", {
                listWords,
                descWords,
                moduleName: moduleName,
                seriesFormatChecked,
              });
              return;
            }
            commit("SET_SELECTED_FILTER", {
              moduleName,
              filterData: {
                ...state[moduleName].filterData,
                seriesId: series.id,
              },
            });
            dispatch("setIsFiltered", { moduleName, payload: true });
          }
        }
      }
    }

    commit("SET_SEARCH_LOADING", { moduleName, payload: false });
  },
  // Resolves make/model/series from a series keyword, e.g. "zre18 . front bumper".
  // Returns true when the series is known, false to let the caller fall back to a plain text search.
  async searchBySeriesFormat(
    { state, commit, dispatch },
    { series, descWords, moduleName }
  ) {
    let data;
    try {
      const result = await globalSearchSeriesFormat({ series });
      if (result.code !== 200) return false;
      data = result.data;
    } catch (error) {
      return false;
    }

    if (!data || !data.makeId) return false;

    const modelIds = data.modelIds || [];
    const seriesIds = data.seriesIds || [];

    await dispatch("setClearFilter", { moduleName, isSearch: true });
    commit("SET_SELECTED_FILTER", {
      moduleName,
      filterData: {
        ...state[moduleName].filterData,
        makeId: data.makeId,
        modelIds,
        seriesIds,
        // modules that only support a single model/series still read these
        modelId: modelIds[0] || "",
        seriesId: seriesIds[0] || "",
        search: descWords.length > 0 ? descWords[0] : "",
      },
    });
    dispatch("setIsFiltered", { moduleName, payload: true });
    return true;
  },
  setIsFiltered({ commit }, { moduleName, payload }) {
    commit("SET_IS_FILTERED", { moduleName, payload });
  },
  setSearchLoading({ commit }, { moduleName, payload }) {
    commit("SET_SEARCH_LOADING", { moduleName, payload });
  },

  // list price
  setSelectedPriceListDate({ commit }, payload) {
    commit("SET_SELECTED_PRICE_LIST_DATE", payload);
  },
  setShowDatePicker({ commit }, { moduleName, payload }) {
    commit("SET_SHOW_DATE_PICKER", { moduleName, payload });
  },
};

const getters = {
  getSelectedFilter: (state) => (moduleName) => state[moduleName].filterData,
  getIsFiltered: (state) => (moduleName) => state[moduleName].isFiltered,
  getMakeList(state) {
    return state.makeList;
  },
  getSearchLoading: (state) => (moduleName) => state[moduleName].isLoading,

  // list price
  getShowDatePicker: (state) => (moduleName) =>
    state[moduleName].showDatePicker,
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
